<script setup>
import { computed, ref, onMounted, watchEffect, watch } from "vue";
import { useStore } from "vuex";
import { remoteImageStaticUrl } from "common/utils/remote-image";

import Artwork from "./artworkComponents/Artwork.vue";
import Products from "./productComponents/Products.vue";
import Upload from "./uploadComponents/Upload.vue";
import LandingIntro from "./landingComponents/LandingIntro.vue";
import SaleBanner from "./landingComponents/SaleBanner.vue";
import Checkout from "./checkoutComponents/Checkout.vue";
import Previews from "./previewsComponents/Previews.vue";
import ErrorHandler from "./ErrorHandler.vue";
import ArtworkStickyBox from "./artworkComponents/ArtworkStickyBox.vue";
import PodPhone from "./contactsComponents/PodPhone.vue";
import { preloadImages } from "./utils/preload-image";
import ImageReview from "./imageReviewComponents/ImageReview.vue";
import PrintPerfectSection from "./printPerfectSection/PrintPerfectSection.vue";
import ProductDetails from "./productComponents/ProductDetails.vue";
import ArtworkUpload from "./uploadComponents/ArtworkUpload.vue";

const store = useStore();
const builderSection = ref(null);
const currentPage = computed(() => store.getters.currentPage);
const jiffyPrintLogoUrl = computed(() => remoteImageStaticUrl("gen/pod/jiffy-print-logo-2.svg"));
const isLoadingProducts = computed(() => store.getters["loading/fetchProducts"]);
const isSingleProductPage = computed(() => store.getters["product/isSingleProductPage"]);
const selectedComposition = computed(() => store.getters["product/selectedComposition"]);
const artwork = computed(() => store.getters["artwork/artwork"]);
const isUploadLoading = computed(() => store.getters["loading/uploadArtwork"]);
const errors = computed(() => {
  const arr = store.getters["errors/stack"];
  const errorsEnabled = store.getters["errors/errorsEnabled"];
  return arr.length && errorsEnabled ? arr : [];
});
const artworkPreviewReady = computed(() => Boolean(selectedComposition.value));
const showBottomUpload = computed(
  () => !isLoadingProducts.value && !artwork.value && !isUploadLoading.value
);

onMounted(() => {
  // preload products while we are on landing page
  store.dispatch("product/fetchProducts");
  // load current order on landing page to be sure user has the latest order in the history
  store.dispatch("checkout/restoreOrder");
});

watch(selectedComposition, composition => {
  if (!composition) return;
  const surfaces = store.getters["product/selectedCompositionSurfaces"];
  preloadImages(Object.values(surfaces).map(surface => surface.picture));
});

watchEffect(() => {
  const $metaElement = document.head.querySelector("meta[name='theme-color']");
  const themeColor = currentPage.value === "builder" ? "#060647" : "#fff";
  $metaElement.setAttribute("content", themeColor);
});
</script>

<template>
  <div class="pod-build-order">
    <div v-if="currentPage === 'loading'" data-testid="pod-build-order-loading">
      <div class="pod-build-order__logo-wrap">
        <img alt="Logo" class="pod-build-order__logo" :src="jiffyPrintLogoUrl" />
      </div>
    </div>

    <div v-if="currentPage === 'builder'" data-testid="pod-build-order-builder">
      <ProductDetails
        v-if="isSingleProductPage && artworkPreviewReady"
        class="pod-build-order__product-details"
      />

      <template v-if="!isSingleProductPage">
        <LandingIntro />
      </template>

      <div id="pod-build-order-anchor" ref="builderSection" class="pod-build-order__builder">
        <ArtworkStickyBox
          v-if="artworkPreviewReady && isSingleProductPage"
          forbidden-zone=".js-checkout-forbidden-area"
          data-testid="pod-build-order-sticky-box"
        />

        <div class="pod-build-order__upload">
          <ArtworkUpload class="pod-build-order__artwork-upload" />

          <Upload />
        </div>

        <div class="pod-build-order__print-perfect">
          <PrintPerfectSection />
        </div>

        <ArtworkStickyBox
          v-if="artworkPreviewReady && !isSingleProductPage"
          forbidden-zone=".js-artwork-forbidden-area"
          data-testid="pod-build-order-sticky-box"
        />

        <template v-if="!isSingleProductPage">
          <div class="pod-build-order__products">
            <SaleBanner />
            <Products />
          </div>

          <Upload v-if="showBottomUpload" />
        </template>

        <div data-testid="pod-build-order-art-check">
          <div class="pod-build-order__image-review" data-testid="pod-build-order-design-proof">
            <ImageReview />
          </div>

          <div class="js-artwork-forbidden-area">
            <div
              id="artwork-scroll-anchor"
              data-testid="pod-build-order-art-preview"
              class="pod-build-order__artwork-grid"
            >
              <Artwork />

              <div class="pod-build-order__pod-phone">
                <PodPhone />
              </div>

              <Previews />
            </div>

            <div
              id="pod-build-order-checkout"
              class="pod-build-order__checkout"
              data-testid="pod-build-order-checkout"
            >
              <Checkout />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errors.length > 0" data-testid="pod-build-order-errors">
      <ErrorHandler :errors="errors" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.pod-build-order {
  padding-bottom: 120px;
  min-height: 100vh;

  &__logo-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
  }

  &__logo {
    width: 240px;
    height: 52px;
  }

  &__products {
    margin-bottom: 24px;
  }

  &__image-review {
    margin-bottom: 32px;

    @include with-desktop {
      padding: 0 $indent-mobile-constructor;
    }
  }

  &__checkout {
    padding: 0 $indent-mobile-constructor;

    @include with-desktop {
      margin-bottom: 40px;
    }
  }

  &__builder {
    max-width: $container-width;
    margin: 0 auto;
    min-height: 100vh;

    @include with-desktop {
      min-height: 0;
    }
  }

  &__artwork-grid {
    display: grid;
    padding: 0 $indent-mobile-constructor;
    grid-template-columns: 1fr;
    gap: 32px;
    margin: 0 auto 68px;

    @include with-desktop {
      grid-template-columns: 380px minmax(calc(100% - 380px - 20px), 700px); // calc: 380px - sidebar width; 20px - gap between sidebar and main content
      gap: 20px;
      margin-bottom: 68px;
    }
  }

  &__pod-phone {
    margin: 0 -16px;

    @include with-tablet {
      display: none;
    }
  }

  &__product-details {
    padding: 0 $indent-mobile-constructor;
    max-width: $container-width;
    margin: 10px auto;

    @include with-desktop {
      margin: 64px auto 32px;
    }
  }

  &__upload {
    margin-top: 32px;
  }

  &__print-perfect {
    padding: 24px 0;
  }

  &__artwork-upload {
    @include with-desktop {
      display: none;
    }
  }
}
</style>
